import React from 'react';
import {
    Box, Grid, Divider, Typography, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Paper, Chip, FormControlLabel, Checkbox, CircularProgress, Button,
} from '@mui/material';
import Moment from 'moment';
import HTTPManager from '../../utils/httpRequestManager';
import { payrollTotals, money, ticketStatusLabels, voidedServiceTotal } from './payrollMath';
import { downloadEmployeePayrollPdf } from './payrollPdf';

// Read-only payroll detail for one employee (same content as the POS "View" panel, without pay/print).
export default class ReportView extends React.Component {
    httpManager = new HTTPManager();

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            error: '',
            ticket_details: [],
            daywiseData: [],
            showDaySummary: false,
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.getEmployeeTickets();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    getEmployeeTickets() {
        const { empSelected, from_date, to_date, showPartial } = this.props;
        this.httpManager.postRequest('merchant/payout/getPayrollTickets', {
            empid: empSelected.mEmployeeId,
            from_date: Moment(from_date).format('YYYY-MM-DD'),
            to_date: Moment(to_date).format('YYYY-MM-DD'),
            showPartial: showPartial !== false,
        }).then((res) => {
            if (!this.mounted) return;
            const rows = ((res && res.data) || []).slice().sort((a, b) => new Date(a.ticketDate) - new Date(b.ticketDate));
            this.setState({ isLoading: false, ticket_details: rows, daywiseData: this.buildDaywise(rows) });
        }).catch((err) => {
            if (!this.mounted) return;
            this.setState({ isLoading: false, error: (err && err.message) || 'Unable to load service details' });
        });
    }

    buildDaywise(rows) {
        const result = {};
        rows.forEach((item) => {
            if (!item.ticketDate) return;
            const date = Moment(item.ticketDate).format('YYYY-MM-DD');
            if (!result[date]) {
                result[date] = { date, totalTips: 0, totalServicePrice: 0, totalEmployeePercentage: 0 };
            }
            const commission = (item.ticketcommissions || []).reduce((sum, t) => sum + (Number(t.employeePercentage) || 0), 0);
            result[date].totalTips += Number(item.Tips) || 0;
            result[date].totalServicePrice += Number(item.servicePrice) || 0;
            result[date].totalEmployeePercentage += commission;
        });
        return Object.values(result).sort((a, b) => (a.date < b.date ? -1 : 1));
    }

    downloadPdf() {
        const { empSelected, from_date, to_date } = this.props;
        downloadEmployeePayrollPdf(empSelected, this.state.ticket_details, from_date, to_date);
    }

    renderSummaryRow(label, value, opts = {}) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: opts.bold ? 700 : 400, color: opts.color }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: opts.bold ? 700 : 600, color: opts.color }}>{value}</Typography>
            </Box>
        );
    }

    render() {
        const { empSelected, from_date, to_date } = this.props;
        const totals = payrollTotals(empSelected, from_date, to_date);
        const voided = voidedServiceTotal(this.state.ticket_details);
        const { daywiseData, ticket_details } = this.state;

        return (
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">Service Details</Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={this.state.showDaySummary}
                                    onChange={(e) => this.setState({ showDaySummary: e.target.checked })}
                                />
                            }
                            label="Date & Days Summary"
                        />
                    </Box>

                    {this.state.isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                    )}
                    {!this.state.isLoading && this.state.error && (
                        <Typography color="error" sx={{ py: 2 }}>{this.state.error}</Typography>
                    )}

                    {!this.state.isLoading && !this.state.error && this.state.showDaySummary && (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Service</TableCell>
                                        <TableCell align="right">Commission</TableCell>
                                        <TableCell align="right">Tip</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {daywiseData.map((d) => (
                                        <TableRow key={d.date}>
                                            <TableCell>{Moment(d.date).format('DD MMM — ddd')}</TableCell>
                                            <TableCell align="right">{money(d.totalServicePrice)}</TableCell>
                                            <TableCell align="right">{money(d.totalEmployeePercentage)}</TableCell>
                                            <TableCell align="right">{money(d.totalTips)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {daywiseData.length > 0 && (
                                        <TableRow>
                                            <TableCell><b>Total</b></TableCell>
                                            <TableCell align="right"><b>{money(daywiseData.reduce((s, d) => s + d.totalServicePrice, 0))}</b></TableCell>
                                            <TableCell align="right"><b>{money(daywiseData.reduce((s, d) => s + d.totalEmployeePercentage, 0))}</b></TableCell>
                                            <TableCell align="right"><b>{money(daywiseData.reduce((s, d) => s + d.totalTips, 0))}</b></TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {!this.state.isLoading && !this.state.error && (
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date / Ticket</TableCell>
                                        <TableCell>Service</TableCell>
                                        <TableCell align="right">Commission</TableCell>
                                        <TableCell align="right">Tip</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ticket_details.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No services in this period</TableCell>
                                        </TableRow>
                                    )}
                                    {ticket_details.map((row) => (
                                        <TableRow key={row.ticketServiceId}>
                                            <TableCell>
                                                <div>{Moment(row.ticketDate).format('MM/DD/YYYY')}</div>
                                                <Typography variant="caption" color="text.secondary">#{row.ticketCode}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <div>{row.serviceName}</div>
                                                <Typography variant="caption" color="text.secondary">{money(row.servicePrice)}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {(row.ticketcommissions || []).length === 0 && '-'}
                                                {(row.ticketcommissions || []).map((t) => (
                                                    <div key={t.id} style={{ color: Number(t.employeePercentage) < 0 ? '#d32f2f' : '#2e7d32' }}>
                                                        {money(Math.abs(Number(t.employeePercentage) || 0))}
                                                        {Number(t.employeePercentage) < 0 && <span style={{ fontSize: 10, marginLeft: 4 }}>(Ded.)</span>}
                                                    </div>
                                                ))}
                                            </TableCell>
                                            <TableCell align="right">{money(row.Tips)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {ticketStatusLabels(row.ticket).map(([label, color]) => (
                                                        <Chip key={label} label={label} color={color} size="small" />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">Financial Summary</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={this.state.isLoading || !!this.state.error}
                            onClick={() => this.downloadPdf()}
                        >
                            Download PDF
                        </Button>
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        {this.renderSummaryRow('Base Service', money((Number(empSelected.TotalServiceAmount) || 0) + voided))}
                        {voided > 0 && this.renderSummaryRow('Voided Amount', '-' + money(voided), { color: '#d32f2f' })}
                        <Divider sx={{ my: 1 }} />
                        {this.renderSummaryRow(`Gross Commission (${empSelected.mEmployeePercentage || 0}%)`, money(totals.service), { bold: true })}
                        {this.renderSummaryRow('Total Tips', '+' + money(totals.tips), { color: '#2e7d32' })}
                        {this.renderSummaryRow('Total Discounts', '-' + money(totals.discounts), { color: '#d32f2f' })}
                        {totals.hasSalary && this.renderSummaryRow(`Fixed Salary (${empSelected.salary_type || 'Weekly'})`, money(empSelected.minimumSalary))}
                        {totals.hasSalary && this.renderSummaryRow('Salary Offset', '+' + money(totals.salaryOffset), { color: '#2e7d32' })}
                        <Divider sx={{ my: 1 }} />
                        {this.renderSummaryRow('Net Payable', money(totals.netPayable), { bold: true })}

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Payment Split</Typography>
                        {this.renderSummaryRow(`Cash (${empSelected.mCashPercentage || 0}%)`, money(totals.payCash))}
                        {this.renderSummaryRow(`Check (${empSelected.mCheckPercentage || 0}%)`, money(totals.payCheck))}

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Tips</Typography>
                        {this.renderSummaryRow('Tips by Cash', money(totals.tipsCash))}
                        {this.renderSummaryRow('Tips by Check', money(totals.tipsCheck))}

                        <Divider sx={{ my: 1 }} />
                        {this.renderSummaryRow('Final Cash Payment', money(totals.finalCash), { bold: true })}
                        {this.renderSummaryRow('Final Check Payment', money(totals.finalCheck), { bold: true })}
                    </Paper>
                </Grid>
            </Grid>
        );
    }
}
